import axios, { AxiosError } from 'axios';
import { formatAuthHeader } from '../utils/authUtils';
import { setupTokenRefreshInterceptor } from '../utils/tokenRefreshInterceptor';
import { UserBrand } from '../types/brand';

const AUTH_API_BASE_URL = '/api/auth';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: {
    id: string;
    username: string;
    email: string;
    full_name?: string;
  };
}

interface RegisterResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  user: {
    id: string;
    username: string;
    email: string;
    full_name?: string;
  };
}

interface RefreshTokenResponse {
  access_token: string;
  /** The backend does NOT rotate refresh tokens — this is never returned
      today; optional so consumers must guard (the interceptor does). */
  refresh_token?: string;
  token_type: string;
}

interface UserData {
  id: string;
  username: string;
  email: string;
  full_name?: string;
  is_active?: boolean;
  is_verified?: boolean;
  is_admin?: boolean;
  is_super_admin?: boolean;
  is_customer_success?: boolean;
  profile_image_url?: string;
  bio?: string;
  brands?: UserBrand[];
  created_at?: string;
  last_login?: string;
}

interface RegisterData {
  username: string;
  email: string;
  password: string;
  full_name?: string;
}

// Create axios instance with default config
const authApiClient = axios.create({
  baseURL: AUTH_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // Increased to 30s due to slow health check on backend
});

// Setup automatic token refresh interceptor
setupTokenRefreshInterceptor(authApiClient);

// Request interceptor for error handling
authApiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error status
      console.error('Auth API Error:', error.response.status, error.response.data);
    } else if (error.request) {
      // Request made but no response
      console.error('Auth API No Response:', error.request);
    } else {
      // Error in request configuration
      console.error('Auth API Request Error:', error.message);
    }
    return Promise.reject(error);
  }
);

/**
 * Login user with username and password
 * @param username - User's username
 * @param password - User's password
 * @returns Login response with tokens and user data
 */
export const login = async (username: string, password: string): Promise<LoginResponse> => {
  try {
    // Backend expects OAuth2 password flow format with form-encoded data
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await authApiClient.post<LoginResponse>('/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Register new user
 * @param userData - Registration data (username, email, password, full_name)
 * @returns Registration response with tokens and user data
 */
export const register = async (userData: RegisterData): Promise<RegisterResponse> => {
  try {
    const response = await authApiClient.post<RegisterResponse>('/register', userData);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      const detail = error.response.data.detail;
      // Handle array of validation errors (FastAPI 422 response)
      if (Array.isArray(detail)) {
        const messages = detail.map((err: { msg?: string; message?: string }) => err.msg || err.message || 'Validation error');
        throw new Error(messages.join('. '));
      }
      // Handle string error message
      if (typeof detail === 'string') {
        throw new Error(detail);
      }
      // Handle object with message property
      if (typeof detail === 'object' && detail.msg) {
        throw new Error(detail.msg);
      }
      throw new Error('Registration failed');
    }
    throw error;
  }
};

/**
 * Logout user
 * @param token - Access token
 * @returns Success message
 */
export const logout = async (token: string, refreshToken?: string): Promise<{ message: string }> => {
  try {
    const response = await authApiClient.post<{ message: string }>(
      '/logout',
      refreshToken ? { refresh_token: refreshToken } : {},
      {
        headers: {
          Authorization: formatAuthHeader(token),
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Refresh access token using refresh token
 * @param refreshToken - Refresh token
 * @returns New access and refresh tokens
 */
export const refreshToken = async (refreshToken: string): Promise<RefreshTokenResponse> => {
  try {
    const response = await authApiClient.post<RefreshTokenResponse>('/refresh', {
      refresh_token: refreshToken,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Get current user data
 * @param token - Access token
 * @returns User data
 */
export const getCurrentUser = async (token: string): Promise<UserData> => {
  try {
    const response = await authApiClient.get<{ data: UserData }>('/me', {
      headers: {
        Authorization: formatAuthHeader(token),
      },
    });
    return response.data.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Verify token validity
 * @param token - Access token to verify
 * @returns True if token is valid, false otherwise
 */
export const verifyToken = async (token: string): Promise<boolean> => {
  try {
    await authApiClient.post(
      '/verify',
      {},
      {
        headers: {
          Authorization: formatAuthHeader(token),
        },
      }
    );
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Change user password
 * @param token - Access token
 * @param oldPassword - Current password
 * @param newPassword - New password
 * @returns Success message
 */
export const changePassword = async (
  token: string,
  oldPassword: string,
  newPassword: string
): Promise<{ message: string }> => {
  try {
    const response = await authApiClient.post<{ message: string }>(
      '/change-password',
      {
        current_password: oldPassword,
        new_password: newPassword,
      },
      {
        headers: {
          Authorization: formatAuthHeader(token),
        },
      }
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Request password reset email
 * @param email - User's email address
 * @returns Success message
 */
export const forgotPassword = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await authApiClient.post<{ message: string }>(
      '/forgot-password',
      { email }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Reset password using token from email
 * @param token - Reset token from email link
 * @param newPassword - New password to set
 * @returns Success message
 */
export const resetPassword = async (
  token: string,
  newPassword: string
): Promise<{ message: string; username?: string }> => {
  try {
    const response = await authApiClient.post<{ message: string; username?: string }>(
      '/reset-password',
      {
        token,
        new_password: newPassword,
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Send welcome email to a user (SuperAdmin only)
 * @param userId - ID of the user to send welcome email to
 * @returns Success message
 */
export const sendWelcomeEmail = async (userId: string): Promise<{ message: string }> => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await authApiClient.post<{ message: string }>(
      '/send-welcome-email',
      { user_id: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Toggle super admin status for a user (SuperAdmin only)
 * @param userId - ID of the user to toggle
 * @param isSuperAdmin - New super admin status
 * @returns Success message with updated status
 */
export const toggleSuperAdmin = async (
  userId: string,
  isSuperAdmin: boolean
): Promise<{ message: string; user_id: string; is_super_admin: boolean }> => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await authApiClient.post<{ message: string; user_id: string; is_super_admin: boolean }>(
      '/toggle-super-admin',
      { user_id: userId, is_super_admin: isSuperAdmin },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Toggle Customer Success status for a user (SuperAdmin only)
 * @param userId - ID of the user to toggle
 * @param isCustomerSuccess - New Customer Success status
 * @returns Success message with updated status
 */
export const toggleCustomerSuccess = async (
  userId: string,
  isCustomerSuccess: boolean
): Promise<{ message: string; user_id: string; is_customer_success: boolean }> => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await authApiClient.post<{ message: string; user_id: string; is_customer_success: boolean }>(
      '/toggle-customer-success',
      { user_id: userId, is_customer_success: isCustomerSuccess },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Remove a user account (SuperAdmin only). Deletes the account, tokens,
 * and brand/org associations; keeps events, stats, and their content.
 */
export const removeUser = async (
  userId: string
): Promise<{ message: string; user_id: string; brand_associations_removed: number }> => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await authApiClient.post<{ message: string; user_id: string; brand_associations_removed: number }>(
      '/remove-user',
      { user_id: userId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export default {
  login,
  register,
  logout,
  refreshToken,
  getCurrentUser,
  verifyToken,
  changePassword,
  forgotPassword,
  resetPassword,
  sendWelcomeEmail,
  toggleSuperAdmin,
  toggleCustomerSuccess,
  removeUser,
};
